import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect, Suspense } from "react";
import userStore from "../../../zustand/userStore";
import { Grid, Paper, Typography, Box, Button } from "@mui/material";
import { getDocumentsData } from "../../EventOrganiser/redux/eventOrganiserThunks";

const Documents = () => {
  const location = useLocation();
  const { state } = location || {};
  const { eventId, activityId } = state || {};
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = userStore((state) => state.user);

  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const { documentList = [] } = useSelector((store) => store.eventOrganiser);

  const staticCategories = [
    // { categoryName: "Certificates/प्रमाणपत्र", categoryId: [28, 136] },
    // { categoryName: "Notices/सूचना", categoryId: [131, 137] },
    { categoryName: "Request Letters/अनुरोध पत्र", categoryId: [134, 138] },
    { categoryName: "Applications/आवेदन", categoryId: [132, 139] },
    // { categoryName: "Agreements/अनुबंध", categoryId: [60, 140] },
    // {
    //   categoryName: "General Correspondences/सामान्य पत्राचार",
    //   categoryId: [135, 46],
    // },
    // { categoryName: "POSH/आलीशान", categoryId: [54, 0] },
    // { categoryName: "HR Documents/एचआर दस्तावेज़", categoryId: [32, 0] },
  ];

  useEffect(() => {
    if (selectedCategoryId) {
      dispatch(
        getDocumentsData({
          type: "schoolModule",
          categoryIds: selectedCategoryId,
        })
      );
    }
  }, [dispatch, selectedCategoryId]);

  useEffect(() => {
    if (staticCategories.length > 0 && !selectedCategoryId) {
      setSelectedCategoryId(staticCategories[0].categoryId);
    }
  }, [selectedCategoryId]);

  const handleTitleClick = (doc) => {
    navigate("/docFormTestRun?formId=" + doc.id, {
      state: { nameTitle: doc?.name_title, eventId, activityId },
    });
  };

  const MAX_VISIBLE_DOCUMENTS = 25;

  // ✅ Find selected category forms
  // const selectedCategoryData = documentList.find(
  //   (cat) => cat.categoryId === selectedCategoryId
  // );
  // const forms =
  //   selectedCategoryData?.forms ||
  //   (Array.isArray(documentList) ? documentList : []);

  const forms = documentList.flatMap((category) => category.forms);

  // ✅ Split by language
  const englishDocs = forms.filter((doc) => doc.languageCode === "ENG");
  const hindiDocs = forms.filter((doc) => doc.languageCode === "HIN");

  const englishVisible = showAll
    ? englishDocs
    : englishDocs.slice(0, MAX_VISIBLE_DOCUMENTS);
  const hindiVisible = showAll
    ? hindiDocs
    : hindiDocs.slice(0, MAX_VISIBLE_DOCUMENTS);

  return (
    <Suspense fallback={<>Loading...</>}>
      <div className="main_container px-5">
        <Box
          sx={{
            width: "100%",
            maxWidth: "1200px",
            margin: "0 auto",
            pb: 8,
            minHeight: "100vh",
          }}
        >
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{ fontWeight: "bold", color: "#606368" }}
          >
            List of Documents
          </Typography>

          {/* 🔹 Category Buttons */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {staticCategories.map((cat) => (
              <Button
                key={cat.categoryId}
                size="small"
                variant={
                  String(selectedCategoryId) === String(cat.categoryId)
                    ? "contained"
                    : "outlined"
                }
                color="success"
                sx={{
                  padding: "2px 8px",
                  fontSize: "0.75rem",
                  minWidth: "auto",
                  textTransform: "none",
                }}
                onClick={() => {
                  setSelectedCategoryId(cat.categoryId);
                  setShowAll(false);
                }}
              >
                {cat.categoryName}
              </Button>
            ))}
          </Box>

          {/* ✅ Render documents by language */}
          {forms.length === 0 ? (
            <Typography
              variant="h6"
              align="center"
              sx={{ mt: 4, color: "gray", fontStyle: "italic" }}
            >
              No Data Found
            </Typography>
          ) : (
            <Grid container spacing={2} alignItems="stretch">
              {/* English */}
              <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
                <Paper
                  elevation={3}
                  sx={{
                    px: 1,
                    flex: 1,
                    minHeight: showAll ? "auto" : "85vh",
                    display: "flex",
                    flexDirection: "column",
                    border: "2px solid #278d27",
                    boxShadow: "0px 8px 10px -6px rgba(0,0,0,0.8)",
                    backgroundColor: "rgba(255, 255, 255, 0.4)",
                    mt: 1,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    English Documents
                  </Typography>
                  {englishVisible.map((doc, index) => (
                    <Typography
                      key={doc.id}
                      variant="body1"
                      sx={{
                        mt: "5px",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      onClick={() => handleTitleClick(doc)}
                    >
                      {index + 1}. {doc.name_title}
                    </Typography>
                  ))}
                </Paper>
              </Grid>

              {/* Hindi */}
              <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
                <Paper
                  elevation={3}
                  sx={{
                    px: 1,
                    flex: 1,
                    minHeight: showAll ? "auto" : "85vh",
                    display: "flex",
                    flexDirection: "column",
                    border: "2px solid #278d27",
                    boxShadow: "0px 8px 10px -6px rgba(0,0,0,0.8)",
                    backgroundColor: "rgba(255, 255, 255, 0.4)",
                    mt: 1,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Hindi Documents
                  </Typography>
                  {hindiVisible.map((doc, index) => (
                    <Typography
                      key={doc.id}
                      variant="body1"
                      sx={{
                        mt: "5px",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      onClick={() => handleTitleClick(doc)}
                    >
                      {index + 1}. {doc.name_title}
                    </Typography>
                  ))}
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Show More / Show Less */}
          {(englishDocs.length > MAX_VISIBLE_DOCUMENTS ||
            hindiDocs.length > MAX_VISIBLE_DOCUMENTS) && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Button
                variant="contained"
                style={{ backgroundColor: "#278d27" }}
                onClick={() => setShowAll((prev) => !prev)}
              >
                {showAll ? "Show Less" : "Show More"}
              </Button>
            </Box>
          )}
        </Box>
      </div>
    </Suspense>
  );
};

export default Documents;
